import React from 'react';
import { CBadge } from '@coreui/react-pro';

// Tipos de entrega (tabla delivery_types)
export const DELIVERY_TYPES = {
    DELIVERY: 1,
    PICKUP: 2,
    SCHEDULED: 3,
};

export const formatCurrency = (val: number) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val || 0);

// Fechas del backend vienen en hora de Colombia como "YYYY-MM-DD HH:mm:ss"
export const parseDate = (value?: string | null): Date | null => {
    if (!value) return null;
    const date = new Date(value.replace(' ', 'T'));
    return isNaN(date.getTime()) ? null : date;
};

export const formatDateTime = (value?: string | null) => {
    const date = parseDate(value);
    if (!date) return '—';
    return date.toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
};

export const formatTime = (value?: string | null) => {
    const date = parseDate(value);
    if (!date) return '—';
    return date.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: true });
};

export const timeAgo = (value?: string | null) => {
    const date = parseDate(value);
    if (!date) return '';
    const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) return 'hace un momento';
    if (minutes < 60) return `hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `hace ${hours} h`;
    const days = Math.floor(hours / 24);
    return `hace ${days} d`;
};

// Los colores del catálogo vienen en formato Flutter (0xAARRGGBB)
export const flutterColorToHex = (color?: string | null, fallback = '#64748B') => {
    if (!color) return fallback;
    const clean = color.replace(/^0x/i, '').replace('#', '');
    if (clean.length === 8) return '#' + clean.substring(2);
    if (clean.length === 6) return '#' + clean;
    return fallback;
};

const capitalize = (text?: string | null) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

export const StatusBadge: React.FC<{ name?: string | null; color?: string | null }> = ({ name, color }) => {
    const hex = flutterColorToHex(color);
    return (
        <CBadge
            className="px-2 py-1 fw-semibold"
            style={{ backgroundColor: hex + '26', color: hex, border: `1px solid ${hex}66` }}
        >
            ● {capitalize(name)}
        </CBadge>
    );
};

// Riesgo de fraude: solo se marca medio y alto (25 y 50 puntos, OrderRiskUtility en el backend)
export const RISK_LEVELS: Record<'medium' | 'high', { label: string; color: string }> = {
    medium: { label: 'Revisar', color: '#F59E0B' },
    high: { label: 'Riesgo alto', color: '#EF4444' },
};

export const RiskBadge: React.FC<{ level?: string | null; score?: number | null; reasons?: { label: string; points: number }[] }> = ({ level, score, reasons = [] }) => {
    if (level !== 'medium' && level !== 'high') return null;
    const { label, color } = RISK_LEVELS[level];
    const title = [`${label} · ${score ?? 0} puntos`, ...reasons.map((r) => `• ${r.label} (+${r.points})`)].join('\n');
    return (
        <CBadge className="px-2 py-1 fw-semibold" title={title} style={{ backgroundColor: color + '26', color, border: `1px solid ${color}66` }}>
            ⚠ {label}
        </CBadge>
    );
};

export const DeliveryTypeBadge: React.FC<{ typeId: number; name?: string }> = ({ typeId, name }) => {
    switch (Number(typeId)) {
        case DELIVERY_TYPES.DELIVERY:
            return <CBadge color="info-subtle" className="text-info border border-info-subtle">Domicilio</CBadge>;
        case DELIVERY_TYPES.PICKUP:
            return <CBadge color="success-subtle" className="text-success border border-success-subtle">Recoger en tienda</CBadge>;
        case DELIVERY_TYPES.SCHEDULED:
            return <CBadge color="warning-subtle" className="text-warning border border-warning-subtle">Programado</CBadge>;
        default:
            return <CBadge color="secondary-subtle" className="text-secondary border">{name ?? 'Otro'}</CBadge>;
    }
};

export const whatsappLink = (phone?: string | null) => {
    if (!phone) return undefined;
    console.log("phone");
    console.log(phone);
    const digits = phone.toString().replace(/\D/g, '');
    return `https://wa.me/${digits.length === 10 ? '57' + digits : digits}`;
};

// Mensaje del backend para errores que axios lanza (ej. 404) o respuestas con status != 200
export const getApiErrorMessage = (errorOrResponse: any, fallback: string) =>
    errorOrResponse?.response?.data?.[0]?.message ?? errorOrResponse?.message ?? fallback;
